import { ProfileBadge } from "../../../../components/world/rmc/ProfileBadge";
import Button from "../../../../components/world/rmc/Button";
import { FullModal } from "./fullModal";
import ProgressBar from "../../../../components/world/rmc/ProgressBar";
import clsx from "clsx";
import { useNavigate } from "react-router-dom";
import { calculateBadgeProgress } from "../../../../utils";
import { useContext, useEffect, useRef } from "react";
import { useGetAudience } from "../../../../api/audience/useGetAudience";
import { RmcContext } from "../../../../components/context/RmcContext";
import { Avatar } from "../../../../components/Avatar";

const scoreBreakdown = (results = {}) => {
  return [
    {
      id: "quiz-score",
      label: "Quiz Score",
      points: Number(results.quiz_point) || 0,
      type: "primary",
    },
    {
      id: "high-score-bonus",
      label: "High Score Bonus",
      points: results.high_score_bonus,
      type: "bonus",
    },
    {
      id: "daily-play-bonus",
      label: "Daily Play Bonus",
      points: results.daily_bonus,
      type: "bonus",
    },
  ].filter((item) => item.points !== null && item.points !== undefined);
};

export const GameOverModal = ({ results, triviaId, brandId }) => {
  const navigate = useNavigate();
  const hasFetched = useRef(false);
  const scoreData = scoreBreakdown(results);
  const { GetAudience } = useGetAudience();
  const { rmcAudience, slug } = useContext(RmcContext);

  const progress = calculateBadgeProgress(
    results.audience_points,
    results.current_badge,
    results.next_badge
  );
  useEffect(() => {
    if (brandId && !hasFetched.current) {
      hasFetched.current = true;
      GetAudience(brandId, false);
    }
  }, [brandId]);
  const name = `${rmcAudience.first_name} ${rmcAudience.last_name}`;

  return (
    <FullModal className="fixed inset-0 z-[4000] bg-[#111522] animate-slide-up 480:max-w-[440px] 480:mx-auto overflow-y-auto hidden-scrollbar">
      <div className="px-4 pt-16 relative flex flex-col h-screen">
        <div className="flex-1">
          <p className="text-xl font-semibold text-center text-white mb-10 ">
            Game over
          </p>
          <div className=" grid justify-items-center px-4">
            {rmcAudience?.user_image ? (
              <ProfileBadge
                image={rmcAudience?.user_image}
                className="w-24 h-24 p-[2px]"
              />
            ) : (
              <Avatar
                name={name}
                size="xl"
                customBgColor="#FFD700"
                customTextColor="#000"
              />
            )}
            <div className="mt-3 mb-2">
              <p className="text-xl font-semibold text-center text-white mb-1 ">
                {results?.quiz_point || 0} pts
              </p>
              <div className="bg-blackhue-rmc_hue py-2 px-4 rounded-[120px] text-center ">
                <p className="text-[#FFFFFFBF] text-xs text-center font-medium ">
                  {`${results?.correct_answers_count} out of ${results?.total_questions_count} questions correct!`}
                </p>
              </div>
            </div>
            <div className="mt-3.5 w-full mb-8">
              <ProgressBar
                currPoints={progress.currentBadge.points}
                totalPoints={progress.nextBadge.points}
                percentage={progress.progressPercentage}
                type="badge"
                currentlevel={progress.currentBadge.name}
                nextlevel={progress.nextBadge.name}
                hideNextPoints={!results.next_badge}
                centerLabel={`${results.audience_points || 0} pts`}
              />
            </div>
            <div className="flex flex-col gap-4 w-full my-10">
              {scoreData.map((x, i) => {
                return (
                  <div
                    key={i}
                    className="border border-[#252B41] rounded-md overflow-hidden flex items-center "
                  >
                    <div className="flex-1 bg-blackhue-rmc_hue p-4 text-base font-semibold text-[#FFFFFFBF]">
                      {x.label}
                    </div>
                    <div className="bg-transparent flex-1 p-4 pe-8 text-end text-base font-semibold text-[#FFFFFFBF]">
                      {x.points}pts
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="flex justify-between items-center  bottom-10 left-5 right-5 480:left-auto 480:right-auto mb-20">
            <button
              onClick={() => {
                sessionStorage.removeItem("gameOverActive");
                navigate(
                  `/worlds/${slug}/rules?id=${brandId}&trivia_id=${triviaId}`
                );
              }}
              className={clsx(
                "py-3 px-5 flex items-center gap-2 rounded-lg font-medium text-base transition-colors bg-[#FFD700] text-black hover:bg-[#FFD700]/90"
              )}
            >
              <Return /> Play Again
            </button>
            <button
              onClick={() => {
                sessionStorage.removeItem("gameOverActive");
                navigate(`/worlds/${slug}/home?id=${brandId}`);
              }}
              className={clsx(
                "px-5 py-3 flex items-center gap-2 rounded-lg font-medium text-base transition-colors bg-[#1A1F33] text-white "
              )}
            >
              <Cancel /> Exit Game
            </button>
          </div>
        </div>

        <Button
          onClick={() => {
            sessionStorage.removeItem("gameOverActive");
            navigate(`/worlds/${slug}d/achievements?tab=leaderboard`);
          }}
          text="View Leaderboard"
          className="text-lg py-[18px] mb-6 "
        />
      </div>
    </FullModal>
  );
};

const Return = () => {
  return (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M11.896 18C11.896 18.1989 11.817 18.3897 11.6763 18.5303C11.5357 18.671 11.3449 18.75 11.146 18.75C7.354 18.75 4.25 15.745 4.25 12C4.25 8.255 7.354 5.25 11.146 5.25C14.251 5.25 16.895 7.265 17.751 10.051L18.354 9.031C18.4552 8.85967 18.6203 8.73555 18.813 8.68595C19.0057 8.63634 19.2102 8.66532 19.3815 8.7665C19.5528 8.86768 19.6769 9.03278 19.7266 9.22547C19.7762 9.41816 19.7472 9.62267 19.646 9.794L18.016 12.549C17.9162 12.718 17.7541 12.8411 17.5645 12.892C17.375 12.9429 17.173 12.9174 17.002 12.821L14.18 11.23C14.0942 11.1816 14.0187 11.1168 13.958 11.0392C13.8972 10.9617 13.8523 10.8729 13.8258 10.778C13.7994 10.6831 13.7919 10.5839 13.8037 10.4861C13.8156 10.3883 13.8466 10.2938 13.895 10.208C13.9434 10.1222 14.0082 10.0467 14.0858 9.98597C14.1633 9.9252 14.2521 9.8803 14.347 9.85384C14.4419 9.82737 14.5411 9.81986 14.6389 9.83173C14.7367 9.84359 14.8312 9.87461 14.917 9.923L16.389 10.753C15.815 8.465 13.698 6.75 11.147 6.75C8.149 6.75 5.75 9.117 5.75 12C5.75 14.883 8.149 17.25 11.146 17.25C11.3449 17.25 11.5357 17.329 11.6763 17.4697C11.817 17.6103 11.896 17.8011 11.896 18Z"
        fill="black"
      />
    </svg>
  );
};

const Cancel = () => {
  return (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        fill-rule="evenodd"
        clip-rule="evenodd"
        d="M19.7091 5.71002L18.2891 4.29004L11.9991 10.59L5.70905 4.29004L4.28906 5.71002L10.5891 12L4.28906 18.2901L5.70905 19.71L11.9991 13.41L18.2891 19.71L19.7091 18.2901L13.4091 12L19.7091 5.71002Z"
        fill="white"
      />
    </svg>
  );
};
